import { useMemo } from 'react';
import { Dot } from '../../../../components';
import { useGetOrganizationSettingsQuery } from '../../../../services';
import { Benchmarks } from '../../../../types';
import { formatDurationVerbose, getStatCardColor } from '../../../../utils';
import CallDetailsHeaderCard from '../CallDetailsHeaderCard';

const roundToTwoDecimals = (value: number): number => Math.round(value * 100) / 100;

const calculateTalkRatio = (talkTime: number, callDuration: number | null): number =>
  callDuration ? roundToTwoDecimals((talkTime / callDuration) * 100) : 0;

const calculateTalkSpeed = (wordsCount: number, talkTime: number): number =>
  talkTime ? Math.round((wordsCount / talkTime) * 60) : 0;

interface CallStatsPanelProps {
  longestMonologueDuration?: number;
  fillerWordsCount?: number;
  wordsCount?: number;
  talkTime?: number;
  callDuration?: number | null;
}

const CallStatsPanel = ({
  longestMonologueDuration = 0,
  fillerWordsCount = 0,
  wordsCount = 0,
  talkTime = 0,
  callDuration = 0,
}: CallStatsPanelProps) => {
  const { data: orgConfigs } = useGetOrganizationSettingsQuery({});
  const {
    longestMonologue: longestMonologueBenchmark,
    fillerWordsPerMinute: fillerWordsPerMinuteBenchmark,
    wordsPerMinute: wordsPerMinuteBenchmark,
    talkRatio: talkRatioBenchmark,
  } = orgConfigs || {};

  const cards = useMemo(
    () => [
      {
        // Longest time representative spoke without letting the prospect speak.
        type: Benchmarks.LONGEST_MONOLOGUE,
        title: 'Longest monologue',
        value: longestMonologueDuration,
        tooltip: 'Longest monologue by representative',
        benchmark: longestMonologueBenchmark,
        parser: formatDurationVerbose,
      },
      {
        // The % of the call the representative was speaking.
        type: Benchmarks.TALK_RATIO,
        title: 'Talk ratio',
        value: calculateTalkRatio(talkTime, callDuration),
        tooltip: 'Talk ratio, % representative',
        benchmark: talkRatioBenchmark,
        parser: (value: number) => `${value}%`,
      },
      {
        // How many words per minute the representative spoke.
        type: Benchmarks.TALK_SPEED,
        title: 'Talk speed',
        value: calculateTalkSpeed(wordsCount, talkTime),
        tooltip: 'Talk speed, words per minute',
        benchmark: wordsPerMinuteBenchmark,
      },
      {
        // How many filler words the representative spoke per minute.
        type: Benchmarks.FILLER_WORDS,
        title: 'Filler words',
        value: fillerWordsCount,
        tooltip: 'Filler words, filler words per minute',
        benchmark: fillerWordsPerMinuteBenchmark,
      },
    ],
    [
      longestMonologueDuration,
      talkTime,
      callDuration,
      wordsCount,
      fillerWordsCount,
      longestMonologueBenchmark,
      talkRatioBenchmark,
      wordsPerMinuteBenchmark,
      fillerWordsPerMinuteBenchmark,
    ]
  );

  return (
    <div className="flex gap-4">
      {cards.map((card) => {
        const indicatorColor = getStatCardColor({ value: card.value, type: card.type, benchmark: card.benchmark });
        return (
          <CallDetailsHeaderCard
            key={card.type}
            title={card.title}
            value={card.parser?.(card.value) ?? card.value}
            tooltip={card.tooltip}
            indicator={indicatorColor ? <Dot color={indicatorColor} /> : undefined}
          />
        );
      })}
    </div>
  );
};

export default CallStatsPanel;
